import type { Calendar, Slot, Term, Unit } from "./types";
import { DAYS } from "./planner";
import { parseWeeks } from "./weeks";
import { fnv } from "./text";

const KIND_BS: Record<string, string> = { P: "Predavanje", AV: "Auditorne vježbe", LV: "Laboratorijske vježbe", S: "Seminar", T: "Tutorijal", other: "Nastava", custom: "" };

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
const local = (d: Date, m: number) => `${ymd(d)}T${pad(Math.floor(m / 60))}${pad(m % 60)}00`;
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const esc = (s: unknown) => String(s ?? "").replace(/\\/g, "\\\\").replace(/[;,]/g, (c) => "\\" + c).replace(/\r?\n/g, "\\n");
const enc = new TextEncoder();
const fold = (line: string) => {
  const out: string[] = [];
  let cur = "", bytes = 0;
  for (const ch of line) {
    const n = enc.encode(ch).length;
    if (bytes + n > (out.length ? 74 : 75)) { out.push(cur); cur = " "; bytes = 1; }
    cur += ch; bytes += n;
  }
  out.push(cur);
  return out.join("\r\n");
};

const VTZ = ["BEGIN:VTIMEZONE", "TZID:Europe/Sarajevo",
  "BEGIN:DAYLIGHT", "TZOFFSETFROM:+0100", "TZOFFSETTO:+0200", "TZNAME:CEST", "DTSTART:19700329T020000", "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU", "END:DAYLIGHT",
  "BEGIN:STANDARD", "TZOFFSETFROM:+0200", "TZOFFSETTO:+0100", "TZNAME:CET", "DTSTART:19701025T030000", "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU", "END:STANDARD",
  "END:VTIMEZONE"];

export interface IcsOptions {
  term: Term;
  units: Map<string, Unit>;
  calendar: Calendar;
  cycleOf?: (slot: Slot) => number | null | undefined;
  fallbackRange?: [string, string];
  name?: string;
  domain?: string;
}

export function termRange(o: IcsOptions, slot: Slot): [Date, Date] {
  const u = slot.fac ? o.units.get(slot.fac) : undefined;
  const cyc = String(o.cycleOf?.(slot) ?? 1);
  const r = u?.semesters?.[o.term]?.[cyc] ?? u?.semesters?.[o.term]?.["1"] ?? o.fallbackRange
    ?? (o.term === "winter" ? ["2026-10-05", "2027-01-15"] : ["2027-02-22", "2027-06-04"]);
  return [new Date(r[0] + "T00:00:00"), new Date(r[1] + "T00:00:00")];
}

export function buildIcs(slots: Slot[], o: IcsOptions): string {
  const holidays = new Set(o.calendar.holidays.map((h) => h.date));
  const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z";
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Slusam//UNSA//BS", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    `X-WR-CALNAME:${esc(o.name || "Raspored")}`, "X-WR-TIMEZONE:Europe/Sarajevo", ...VTZ];
  for (const s of slots) {
    const [from, to] = termRange(o, s);
    const first = new Date(from);
    const want = (DAYS.indexOf(s.day) + 1) % 7;
    while (first.getDay() !== want) first.setDate(first.getDate() + 1);
    if (first > to) continue;
    const weeks = parseWeeks(s.weeks);
    const ex: string[] = [];
    for (let d = new Date(first), w = 1; d <= to; d.setDate(d.getDate() + 7), w++) {
      if (holidays.has(iso(d)) || (weeks && !weeks.has(w))) ex.push(local(d, s.start));
    }
    const until = `${ymd(to)}T215959Z`;
    const desc = [KIND_BS[s.kind], s.group && s.group !== "-" ? s.group : "", s.lecturer, s.note,
      s.academicYear && s.academicYear !== o.calendar.academic_year ? `Termin iz rasporeda ${s.academicYear}, provjeriti` : ""].filter(Boolean).join("\n");
    const unit = s.fac ? o.units.get(s.fac) : undefined;
    lines.push("BEGIN:VEVENT", `UID:${fnv(s.id)}-${fnv(s.id + "x")}@${o.domain || "slusam"}`, `DTSTAMP:${stamp}`,
      `DTSTART;TZID=Europe/Sarajevo:${local(first, s.start)}`, `DTEND;TZID=Europe/Sarajevo:${local(first, s.end)}`,
      `RRULE:FREQ=WEEKLY;UNTIL=${until}`,
      ...(ex.length ? [`EXDATE;TZID=Europe/Sarajevo:${ex.join(",")}`] : []),
      `SUMMARY:${esc(s.kind === "custom" ? s.title : `${s.title} (${s.kind})`)}`,
      `LOCATION:${esc([s.room, unit?.name, unit?.address].filter(Boolean).join(", "))}`,
      `DESCRIPTION:${esc(desc)}`, "END:VEVENT");
  }
  lines.push("END:VCALENDAR");
  return lines.map(fold).join("\r\n") + "\r\n";
}
